import * as React from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Typography, makeStyles, Button, Collapse, Grid } from '@material-ui/core';
import { useIifImportSelector, IifImportSelectors } from '../store/modules/iifImport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { DateTime } from 'luxon'
import { classNames } from '../shared';
import JournalEntryLineViews from './JournalEntryLinesView';
import AudCurrencyFormatter from '../utilities/currencyFormatter';
import { ApplicationSettingsSelectors, useApplicationSettingsSelector, TaxMode } from '../store/modules/applicationSettings';
import { red, yellow, green } from '@material-ui/core/colors';
import AttachmentViews from './AttachmentsView';

interface IJournalEntryViewProps {
    fileIndex: number;
    journalEntryIndex: number;
    journalEntryPosition: number;
    importSuccess: boolean;
    importError: boolean;
}

const useStyles = makeStyles(theme => ({
    error: {
        background: `${red[100]} !important`
    },
    financialError: {
        background: `${theme.palette.warning.dark} !important`,
        color: `${theme.palette.common.white} !important`
    },
    warning: {
        '& td': {
            background: yellow[100]
        }
    },
    errorRow: {
        '& td': {
            background: red[100]
        }
    },
    successRow: {
        '& td': {
            background: green[100]
        }
    },
    button: {
        textAlign: 'center',
        minWidth: 'auto',
        padding: theme.spacing(1),

        '& svg': {
            transition: 'all 0.25s linear'
        }
    },
    expanded: {
        '& svg': {
            transform: 'rotate(180deg)'
        }
    },
    noPadding: {
        padding: theme.spacing(0),
        paddingRight: `${theme.spacing(0)} !important`
    },
    padding: {
        padding: theme.spacing(2)
    }
}))

const JournalEntryView: React.FunctionComponent<IJournalEntryViewProps> = ({
    fileIndex,
    journalEntryIndex,
    journalEntryPosition,
    importError,
    importSuccess
}) => {
    const journalEntry = useIifImportSelector(state => IifImportSelectors.selectJournalEntry(state, journalEntryIndex));

    const taxMode = useApplicationSettingsSelector(ApplicationSettingsSelectors.selectTaxMode);

    const classes = useStyles();
    const [expanded, setExpanded] = React.useState(false);
    const [show, setShow] = React.useState(false);

    const onExpandClick = React.useCallback(() => {
        if (expanded) {
            setShow(!show);
        }
        else {
            setExpanded(true);
            setShow(true);
        }
    }, [setShow, show, expanded, setExpanded]);

    const onExited = React.useCallback(() => {
        setExpanded(false);
    }, [setExpanded])

    if (!journalEntry) {
        return null;
    }

    return (
        <>
            <TableRow className={classNames({ [classes.warning]: journalEntry.lineErrors || journalEntry.attachmentErrors || !journalEntry.attachmentsDownloaded || journalEntry.financialYearError }, { [classes.successRow]: importSuccess }, { [classes.errorRow]: importError })}>
                <TableCell>
                    <Button
                        className={classNames(classes.button, { [classes.expanded]: show })}
                        color="inherit"
                        onClick={onExpandClick}
                    >
                        <FontAwesomeIcon icon={faChevronDown} fixedWidth />
                    </Button>
                </TableCell>
                <TableCell>{journalEntryPosition}</TableCell>
                <TableCell className={classNames({ [classes.financialError]: journalEntry.financialYearError })}>{journalEntry.transactionDate ? DateTime.fromISO(journalEntry.transactionDate).toFormat('dd/MM/yyyy') : null}</TableCell>
                <TableCell>{journalEntry.docNumber}</TableCell>
                <TableCell className={classNames({ [classes.error]: journalEntry.currencyError })}>{journalEntry.currency?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: journalEntry.amountError })} align="right">
                    {
                        taxMode === TaxMode.Excluded ?
                            AudCurrencyFormatter.format(journalEntry.lineAmount ?? 0) :
                            AudCurrencyFormatter.format(journalEntry.lineTotal ?? 0)
                    }
                </TableCell>
            </TableRow>
            {
                expanded && (
                    <TableRow>
                        <TableCell className={classes.noPadding} />
                        <TableCell colSpan={5} className={classes.noPadding}>
                            <Collapse in={show} appear={true} onExited={onExited}>
                                <JournalEntryLineViews
                                    journalEntryLineView={journalEntry.lineView}
                                    creditAmount={journalEntry.lineCreditAmount ?? 0}
                                    debitAmount={journalEntry.lineDebitAmount ?? 0}
                                    amount={journalEntry.lineAmount ?? 0}
                                    tax={journalEntry.lineTax ?? 0}
                                    creditTax={journalEntry.lineCreditTax ?? 0}
                                    debitTax={journalEntry.lineDebitTax ?? 0}
                                    total={journalEntry.lineTotal ?? 0}
                                    creditTotal={journalEntry.lineCreditTotal ?? 0}
                                    debitTotal={journalEntry.lineDebitTotal ?? 0}
                                />
                                <AttachmentViews
                                    fileIndex={fileIndex}
                                    type="journalEntries"
                                    typeIndex={journalEntryIndex}
                                    attachmentView={journalEntry.attachmentView}
                                />
                            </Collapse>
                        </TableCell>
                    </TableRow>
                )
            }
        </>
    );
}

export default JournalEntryView;


// WEBPACK FOOTER //
// ./src/components/JournalEntryView.tsx